import { useContext, useState, useEffect } from 'react';
import AppContext from '../contexts/AppContext';
import DataContext from '../contexts/DataContext';
import Input from './Input';
import Button from './Button';
import PhoneNumberInput from './PhoneNumberInput';
import { useNavigate } from 'react-router-dom';
import ConfigContext from '../contexts/ConfigContext';

export default function ContactInformation({
  scrollToRef,
}: {
  scrollToRef: any;
}) {
  const { isDebrandedVersion, features } = useContext(ConfigContext);
  const {
    slides,
    currentSlide,
    updateSelectedOption,
    urlParams,
    showClickToCall,
  } = useContext(AppContext);
  const { data, updateData } = useContext(DataContext);
  const navigate = useNavigate();
  const [nameHaveError, setNameHaveError] = useState(false);
  const [phoneInputValue, setPhoneInputValue] = useState(
    slides[currentSlide].additionalFields.phone || ''
  );

  useEffect(() => {
    setTimeout(
      scrollToRef?.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'center',
      })
    );
  }, [scrollToRef]);

  const handleNameInput = (e: any) => {
    const name = e.target.value.trim();
    const nameArray = name.split(' ');

    if (nameArray.length >= 2) {
      updateSelectedOption(slides[currentSlide].key, name);
      updateData({
        ...data,
        fullName: name,
      });
      setNameHaveError(false);
    } else {
      updateSelectedOption(slides[currentSlide].key, null);
      updateData({
        ...data,
        fullName: null,
      });
      setNameHaveError(true);
    }
  };

  const handlePhoneInput = (newValue: any, haveError: boolean) => {
    setPhoneInputValue(newValue);
    const newData = haveError ? null : newValue;
    updateSelectedOption(slides[currentSlide].key, newData, 'phone');
    updateData({
      ...data,
      phone: newData,
    });
  };

  const submitForm = () => {
    dataLayer.push({ event: 'seform_contact_information_entered' });

    if (process.env.REACT_APP_ENV !== 'local') {
      const lastSubmittedTime = localStorage.getItem('slf-last-submitted');
      if (lastSubmittedTime) {
        const now = Date.now();
        const secondsPassed = (now - Number(lastSubmittedTime)) / 1000;
        if (secondsPassed < 60 * 5) return;
      }
      localStorage.setItem('slf-last-submitted', String(Date.now()));
    }
    navigate(`/calculating${urlParams}`);
  };

  return (
    <div>
      <p className="mt-[30px] text-[17px] short:!text-[16px] md:text-[18px]">
        Full name
      </p>
      <Input
        name="full_name"
        placeholder="First and last name"
        onChange={(e: any) => handleNameInput(e)}
        className="mt-3 md:mt-4 mb-6 md:mb-6 md:w-[400px]"
        errorMessage="Please enter first and last name"
        haveError={nameHaveError}
        defaultValue={slides[currentSlide].selectedValue}
      />

      <PhoneNumberInput
        label="Phone"
        value={phoneInputValue}
        onChange={handlePhoneInput}
      />

      {showClickToCall ||
      ['ca', 'tx', 'fl'].includes(data.selectedState.toLowerCase()) ? (
        <p className="text-[11px] md:text-xs mr-0 md:mr-[80px]">
          <label>
            <input type="hidden" id="leadid_tcpa_disclosure" />* We’re here to
              help! Each time that you fill out this form, you agree to be
              contacted by phone call, pre-recorded voice, and/or text message
              at the telephone or mobile number that you entered above by
              Enphase Energy, Inc. and by{' '}
              <a
                href="/solar-installers/"
                target="_blank"
                rel="noreferrer"
                className="underline">
                one (1) other company
              </a>{' '}
              about solar and energy storage products and services. These
              calls/messages may be sent using automated telephone technology,
              even if your telephone or mobile number is currently listed on any
              state, federal or corporate “Do Not Call” list. Message and data
              rates may apply. You are not required to give your consent here as
              a condition of any purchase.
          </label>{' '}
          I understand the{' '}
          <a
            href={`${
              isDebrandedVersion
                ? '/tos#privacy'
                : 'https://enphase.com/legal/privacy-policy'
            }`}
            target="_blank"
            rel="noreferrer"
            className="underline">
            Privacy Policy
          </a>{' '}
          and agree to the{' '}
          <a
            href={`${
              isDebrandedVersion
                ? '/tos'
                : 'https://enphase.com/legal/terms-of-service'
            }`}
            target="_blank"
            rel="noreferrer"
            className="underline">
            Terms & Conditions
          </a>
          .
        </p>
      ) : (
        <p className="text-[11px] md:text-xs mr-0 md:mr-[80px]">
          <label>
            <input type="hidden" id="leadid_tcpa_disclosure" />* We’re here to
              help! Each time that you fill out this form, you agree to be
              contacted by phone call, pre-recorded voice, and/or text message
              at the telephone or mobile number that you entered above by
              Enphase Energy, Inc. and by{' '}
              <a
                href="/solar-installers/"
                target="_blank"
                rel="noreferrer"
                className="underline">
                one (1) other company
              </a>{' '}
              about solar and energy storage products and services. These
              calls/messages may be sent using automated telephone technology,
              even if your telephone or mobile number is currently listed on any
              state, federal or corporate “Do Not Call” list. Message and data
              rates may apply. You are not required to give your consent here as
              a condition of any purchase.
          </label>{' '}
          I understand the{' '}
          <a
            href={`${
              isDebrandedVersion
                ? '/tos#privacy'
                : 'https://enphase.com/legal/privacy-policy'
            }`}
            target="_blank"
            rel="noreferrer"
            className="underline">
            Privacy Policy
          </a>{' '}
          and agree to the{' '}
          <a
            href={`${
              isDebrandedVersion
                ? '/tos'
                : 'https://enphase.com/legal/terms-of-service'
            }`}
            target="_blank"
            rel="noreferrer"
            className="underline">
            Terms & Conditions
          </a>
          .
        </p>
      )}

      <div className="mt-[18px] md:mt-[40px]">
        <Button
          title="See report and get estimates"
          action={submitForm}
          disabled={
            !slides[currentSlide].selectedValue ||
            !slides[currentSlide].additionalFields.phone
          }
        />
      </div>
    </div>
  );
}
